import { useTranslation } from 'react-i18next';
import { VALUELESS_OPERATORS, VISIBILITY_OPERATORS } from '../../data/visibility';
import type { VisibilityCondition } from '../../data/visibility';

interface VisibilityEditorProps {
  /** Serialised condition as stored on the property; empty means always visible. */
  value: string;
  /** Tokens this property may depend on — its own is excluded by the caller. */
  tokens: string[];
  onChange: (json: string) => void;
}

const field =
  'mt-1 block w-full rounded-sm border border-input bg-surface px-3 py-2 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/22';

function parse(value: string): VisibilityCondition | null {
  if (!value.trim()) return null;
  try {
    const parsed = JSON.parse(value) as VisibilityCondition;
    return parsed.propertyToken ? parsed : null;
  } catch {
    // A condition saved by hand or by an older shape must not break the editor.
    return null;
  }
}

/**
 * DC-11 authoring. The evaluator existed in both languages and the wizard already
 * honoured conditions, but nothing could create one.
 */
export function VisibilityEditor({ value, tokens, onChange }: VisibilityEditorProps) {
  const { t } = useTranslation();

  const condition = parse(value);
  const operator = condition?.operator ?? 'equals';
  const needsValue = !VALUELESS_OPERATORS.includes(operator);

  const update = (next: VisibilityCondition | null) => onChange(next ? JSON.stringify(next) : '');

  return (
    <div>
      <label className="block text-sm">
        <span className="font-medium text-foreground">{t('visibility.title')}</span>
        <select
          value={condition?.propertyToken ?? ''}
          onChange={(event) =>
            update(
              event.target.value
                ? { propertyToken: event.target.value, operator, value: condition?.value ?? '' }
                : null
            )
          }
          className={field}
        >
          <option value="">{t('visibility.always')}</option>
          {tokens.map((token) => (
            <option key={token} value={token}>
              {token}
            </option>
          ))}
        </select>
      </label>

      {condition ? (
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          <label className="text-sm">
            <span className="font-medium text-foreground">{t('visibility.operator')}</span>
            <select
              value={operator}
              onChange={(event) => update({ ...condition, operator: event.target.value })}
              className={field}
            >
              {VISIBILITY_OPERATORS.map((name) => (
                <option key={name} value={name}>
                  {t(`visibility.operators.${name}`)}
                </option>
              ))}
            </select>
          </label>

          {needsValue ? (
            <label className="text-sm">
              <span className="font-medium text-foreground">{t('visibility.value')}</span>
              <input
                type="text"
                value={condition.value ?? ''}
                onChange={(event) => update({ ...condition, value: event.target.value })}
                className={field}
              />
            </label>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
